import { useEffect, useState } from "react";
import Input from "../../../components/input";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrency,
  switchCurrency,
  updateUserProfile,
} from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Backdrop, CircularProgress } from "@mui/material";
import { apiEndPoints } from "../../../hooks/remote/apiEndPoints";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";

const Settings = () => {
  document.title = "Settings - Cloudvantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currency = useSelector((state) => state.user.currency);

  // ============================================================
  // CURRENCY
  // ============================================================

  const [currencyPTag, setCurrencyPTag] = useState("");
  const [currencyButtonText, setCurrencyButtonText] = useState("");

  // ============================================================
  // TWO FACTOR AUTHENTICATION
  // ============================================================

  const [twoFactorMethod, setTwoFactorMethod] = useState("none");
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [savingTwoFactor, setSavingTwoFactor] = useState(false);

  // ============================================================
  // AUTHENTICATOR SETUP
  // ============================================================

  const [authSetupOpen, setAuthSetupOpen] = useState(false);
  const [authSecret, setAuthSecret] = useState("");
  const [authOtpUri, setAuthOtpUri] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [authSetupLoading, setAuthSetupLoading] = useState(false);
  const [authVerifyLoading, setAuthVerifyLoading] = useState(false);

  // ============================================================
  // AUTHENTICATOR BACKUP CODES
  // ============================================================

  const [backupCodes, setBackupCodes] = useState([]);
  const [backupCodesOpen, setBackupCodesOpen] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);

  // ============================================================
  // TWO FACTOR PASSWORD CONFIRMATION
  // ============================================================

  const [pendingTwoFactorMethod, setPendingTwoFactorMethod] =
    useState("");

  const [twoFactorPassword, setTwoFactorPassword] =
    useState("");

  const [
    showTwoFactorPasswordConfirm,
    setShowTwoFactorPasswordConfirm,
  ] = useState(false);

  // ============================================================
  // REQUEST TWO FACTOR CHANGE
  // ============================================================

  const requestTwoFactorChange = (method) => {
    /*
     * If the user is currently using
     * Authenticator, switching away from
     * it requires the account password.
     */
    if (
      twoFactorMethod === "authenticator" &&
      method !== "authenticator"
    ) {
      setPendingTwoFactorMethod(method);
      setTwoFactorPassword("");
      setShowTwoFactorPasswordConfirm(true);

      return;
    }

    handleTwoFactorChange(method);
  };

  // ============================================================
  // START AUTHENTICATOR SETUP
  // ============================================================

  const handleAuthenticatorSetup = async () => {
    /*
     * If Authenticator is already enabled,
     * do not start a completely new setup
     * simply because its radio option was clicked.
     */
    if (twoFactorMethod === "authenticator") {
      return;
    }

    try {
      setAuthSetupLoading(true);

      /*
       * Clear any previous setup/recovery
       * information before starting again.
       */
      setBackupCodes([]);
      setBackupCodesOpen(false);
      setBackupCodesCopied(false);

      const response =
        await apiEndPoints.setupAuthenticator();

      const data =
        response?.data?.data ||
        response?.data ||
        response;

      if (
        data?.statuscode &&
        data.statuscode !== "00"
      ) {
        throw new Error(
          data?.statusmessage ||
            data?.message ||
            "Unable to start authenticator setup"
        );
      }

      setAuthSecret(
        data?.secret || ""
      );

      setAuthOtpUri(
        data?.otpauth_uri ||
          data?.otp_uri ||
          ""
      );

      setAuthCode("");
      setAuthSetupOpen(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.statusmessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start authenticator setup"
      );
    } finally {
      setAuthSetupLoading(false);
    }
  };

  // ============================================================
  // COPY BACKUP CODES
  // ============================================================

  const handleCopyBackupCodes = async () => {
    if (!backupCodes.length) {
      return;
    }

    const codesText =
      backupCodes.join("\n");

    try {
      /*
       * Modern Clipboard API
       */
      if (
        navigator?.clipboard &&
        window?.isSecureContext
      ) {
        await navigator.clipboard.writeText(
          codesText
        );
      } else {
        /*
         * Fallback for browsers/environments
         * where Clipboard API is unavailable.
         */
        const textArea =
          document.createElement("textarea");

        textArea.value = codesText;

        textArea.style.position =
          "fixed";

        textArea.style.left =
          "-999999px";

        textArea.style.top =
          "-999999px";

        document.body.appendChild(
          textArea
        );

        textArea.focus();
        textArea.select();

        document.execCommand(
          "copy"
        );

        textArea.remove();
      }

      setBackupCodesCopied(true);

      toast.success(
        "Backup codes copied to clipboard"
      );
    } catch (error) {
      console.error(
        "Unable to copy backup codes:",
        error
      );

      toast.error(
        "Unable to copy automatically. Please copy the codes manually."
      );
    }
  };

  // ============================================================
  // VERIFY AUTHENTICATOR
  // ============================================================

  const handleVerifyAuthenticatorSetup =
    async () => {
      if (!authCode.trim()) {
        toast.error(
          "Please enter the code from your authenticator app."
        );

        return;
      }

      try {
        setAuthVerifyLoading(true);

        const response =
          await apiEndPoints.setTwoFactorPreference(
            "authenticator",
            authCode.trim()
          );

        const data =
          response?.data?.data ||
          response?.data ||
          response;

        if (
          data?.statuscode &&
          data.statuscode !== "00"
        ) {
          throw new Error(
            data?.statusmessage ||
              data?.message ||
              "Invalid authenticator code"
          );
        }

        /*
         * Authenticator has now been
         * successfully enabled.
         */
        setTwoFactorMethod(
          "authenticator"
        );

        /*
         * Backend returns these codes once
         * authenticator verification succeeds.
         *
         * Keep them in React state only.
         * Do not store in localStorage or
         * sessionStorage.
         */
        const codes =
          Array.isArray(
            data?.backup_codes
          )
            ? data.backup_codes
            : [];

        setBackupCodes(codes);
        setBackupCodesCopied(false);

        /*
         * Close QR / OTP setup.
         */
        setAuthSetupOpen(false);
        setAuthSecret("");
        setAuthOtpUri("");
        setAuthCode("");

        /*
         * Display backup codes immediately.
         */
        if (codes.length > 0) {
          setBackupCodesOpen(true);
        }

        toast.success(
          data?.statusmessage ||
            "Authenticator successfully enabled."
        );
      } catch (error) {
        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            "Unable to verify authenticator code."
        );
      } finally {
        setAuthVerifyLoading(false);
      }
    };

  // ============================================================
  // FETCH CURRENT 2FA PREFERENCE
  // ============================================================

  useEffect(() => {
    const fetchTwoFactorPreference =
      async () => {
        try {
          setTwoFactorLoading(true);

          const response =
            await apiEndPoints.getTwoFactorPreference();

          const data =
            response?.data?.data ||
            response?.data ||
            response;

          const method =
            data?.method ||
            data?.preference ||
            "none";

          setTwoFactorMethod(method);
        } catch (error) {
          console.error(
            "Unable to fetch 2FA preference:",
            error
          );
        } finally {
          setTwoFactorLoading(false);
        }
      };

    fetchTwoFactorPreference();
  }, []);

  // ============================================================
  // CHANGE TWO FACTOR METHOD
  // ============================================================

  const handleTwoFactorChange =
    async (
      method,
      password = null
    ) => {
      try {
        setSavingTwoFactor(true);

        const response =
          await apiEndPoints.setTwoFactorPreference(
            method,
            null,
            password
          );

        const data =
          response?.data?.data ||
          response?.data ||
          response;

        if (
          data?.statuscode &&
          data.statuscode !== "00"
        ) {
          throw new Error(
            data?.statusmessage ||
              data?.message ||
              "Unable to update 2FA preference"
          );
        }

        setTwoFactorMethod(method);

        setShowTwoFactorPasswordConfirm(
          false
        );

        setPendingTwoFactorMethod(
          ""
        );

        setTwoFactorPassword("");

        /*
         * Recovery codes should not remain
         * displayed after switching away
         * from Authenticator.
         */
        if (
          method !==
          "authenticator"
        ) {
          setBackupCodes([]);
          setBackupCodesOpen(false);
          setBackupCodesCopied(false);
        }

        toast.success(
          method === "none"
            ? "Two-factor authentication disabled"
            : method === "email"
            ? "Email two-factor authentication enabled"
            : "Two-factor authentication updated"
        );
      } catch (error) {
        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            "Unable to update two-factor authentication"
        );
      } finally {
        setSavingTwoFactor(false);
      }
    };

  // ============================================================
  // CURRENCY
  // ============================================================

  useEffect(() => {
    if (currency === "NGN") {
      setCurrencyPTag(
        "Nigerian Naira (₦)"
      );

      setCurrencyButtonText(
        "Switch to Dollar ($)"
      );
    }

    if (currency === "USD") {
      setCurrencyPTag(
        "United States Dollar ($)"
      );

      setCurrencyButtonText(
        "Switch to Naira (₦)"
      );
    }
  }, [currency]);

  const handleCurrencySwitch =
    async () => {
      const prefDenomination =
        currency === "NGN"
          ? "dollars"
          : "naira";

      await dispatch(
        switchCurrency({
          pref_deno:
            prefDenomination,
        })
      );

      const newCurrency =
        currency === "NGN"
          ? "USD"
          : "NGN";

      dispatch(
        setCurrency(newCurrency)
      );
    };

  // ============================================================
  // PASSWORD
  // ============================================================

  const [loading, setLoading] =
    useState(false);

  const updateUserProfileForm =
    useFormik({
      initialValues: {
        old_password: "",
        new_password: "",
        confirm_password: "",
      },

      validationSchema:
        Yup.object({
          old_password:
            Yup.string().required(
              "Old Password cannot be empty"
            ),

          new_password:
            Yup.string().required(
              "New Password cannot be empty"
            ),

          confirm_password:
            Yup.string()
              .oneOf(
                [
                  Yup.ref(
                    "new_password"
                  ),
                  null,
                ],
                "Passwords must match"
              )
              .required(
                "Please confirm your password"
              ),
        }),

      onSubmit: async (
        values,
        {
          resetForm,
        }
      ) => {
        setLoading(true);

        const {
          new_password,
        } = values;

        let updateUserProfileFormData =
          {
            userpass:
              new_password,
          };

        const { payload } =
          await dispatch(
            updateUserProfile({
              updateUserProfile:
                updateUserProfileFormData,
              navigate,
            })
          );

        if (
          payload.statuscode ===
          "00"
        ) {
          setLoading(false);

          toast.success(
            "Password updated successfully",
            {
              position:
                "top-right",

              autoClose: 5000,

              hideProgressBar:
                true,

              closeOnClick:
                false,

              pauseOnHover:
                false,

              theme:
                "colored",
            }
          );

          resetForm();
        }
      },
    });

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="px-4 md:px-10 py-6 min-h-screen dark:text-white">

      <p className="crimson-font text-xl font-medium text-primaryBlue">
        Settings
      </p>

      <div className="w-full lg:w-[70%] my-10">

        {/* ==================================================== */}
        {/* SWITCH CURRENCY */}
        {/* ==================================================== */}

        <div>

          <p>
            Switch currency:
          </p>

          <p className="mt-4 text-sm">

            Product prices will be
            displayed in{" "}

            <span className="underline underline-offset-4">
              {currencyPTag}
            </span>

          </p>

          <button
            onClick={
              handleCurrencySwitch
            }
            className="bg-primaryBlue text-white text-xs p-5 rounded-md mt-3"
          >
            {currencyButtonText}
          </button>

        </div>

        <hr className="my-10 dark:border-[#c4c4c432]" />

        {/* ==================================================== */}
        {/* TWO FACTOR AUTHENTICATION */}
        {/* ==================================================== */}

        <div>

          <p className="font-medium">
            Two-Factor Authentication
          </p>

          <p className="mt-2 text-sm text-gray-500">
            Add an extra layer of
            security to your account.
          </p>

          {twoFactorLoading ? (

            <p className="mt-4 text-sm text-gray-500">
              Loading two-factor
              settings...
            </p>

          ) : (

            <div className="grid gap-3 mt-6">

              {/* NONE */}

              <label className="border dark:border-[#c4c4c432] rounded-md p-4 flex items-center gap-3 cursor-pointer">

                <input
                  type="radio"
                  name="twoFactorMethod"
                  value="none"
                  checked={
                    twoFactorMethod ===
                    "none"
                  }
                  onChange={() =>
                    requestTwoFactorChange(
                      "none"
                    )
                  }
                  disabled={
                    savingTwoFactor ||
                    authSetupLoading ||
                    authVerifyLoading
                  }
                />

                <div>

                  <p className="font-medium text-sm">
                    None
                  </p>

                  <p className="text-xs text-gray-500">
                    Sign in using only
                    your email and
                    password.
                  </p>

                </div>

              </label>

              {/* EMAIL */}

              <label className="border dark:border-[#c4c4c432] rounded-md p-4 flex items-center gap-3 cursor-pointer">

                <input
                  type="radio"
                  name="twoFactorMethod"
                  value="email"
                  checked={
                    twoFactorMethod ===
                    "email"
                  }
                  onChange={() =>
                    requestTwoFactorChange(
                      "email"
                    )
                  }
                  disabled={
                    savingTwoFactor ||
                    authSetupLoading ||
                    authVerifyLoading
                  }
                />

                <div>

                  <p className="font-medium text-sm">
                    Email OTP
                  </p>

                  <p className="text-xs text-gray-500">
                    Receive a one-time
                    code on your
                    registered email
                    whenever you sign in.
                  </p>

                </div>

              </label>

              {/* AUTHENTICATOR */}

              <label className="border dark:border-[#c4c4c432] rounded-md p-4 flex items-center gap-3 cursor-pointer">

                <input
                  type="radio"
                  name="twoFactorMethod"
                  value="authenticator"
                  checked={
                    twoFactorMethod ===
                    "authenticator"
                  }
                  onChange={
                    handleAuthenticatorSetup
                  }
                  disabled={
                    savingTwoFactor ||
                    authSetupLoading ||
                    authVerifyLoading
                  }
                />

                <div>

                  <p className="font-medium text-sm">
                    Authenticator App
                  </p>

                  <p className="text-xs text-gray-500">
                    Use an authenticator
                    app to generate
                    verification codes.
                  </p>

                  {authSetupLoading && (

                    <p className="text-xs text-primaryBlue mt-1">
                      Preparing
                      authenticator
                      setup...
                    </p>

                  )}

                </div>

              </label>

            </div>

          )}

          {/* ================================================== */}
          {/* AUTHENTICATOR SETUP */}
          {/* ================================================== */}

          {authSetupOpen && (

            <div className="mt-6 border dark:border-[#c4c4c432] rounded-md p-6">

              <p className="font-medium text-base">
                Set up Authenticator
                App
              </p>

              <p className="text-sm text-gray-500 mt-2">
                Scan this QR code
                with Google
                Authenticator,
                Microsoft
                Authenticator,
                Authy, or another
                compatible
                authenticator app.
              </p>

              {/* QR CODE */}

              {authOtpUri && (

                <div className="mt-6 flex justify-center">

                  <div className="bg-white p-4 rounded-md">

                    <QRCodeSVG
                      value={
                        authOtpUri
                      }
                      size={180}
                    />

                  </div>

                </div>

              )}

              {/* MANUAL SECRET */}

              {authSecret && (

                <div className="mt-6">

                  <p className="text-xs text-gray-500">
                    Can't scan the QR
                    code? Enter this
                    key manually in
                    your authenticator
                    app:
                  </p>

                  <div className="mt-2 bg-gray-100 dark:bg-[#ffffff0d] rounded-md p-3 break-all">

                    <p className="text-sm font-medium font-mono select-all">
                      {authSecret}
                    </p>

                  </div>

                </div>

              )}

              {/* OTP VERIFICATION */}

              <div className="mt-6">

                <Input
                  label="Authenticator verification code"
                  type="text"
                  inputMode="numeric"
                  name="authCode"
                  value={
                    authCode
                  }
                  onChange={(
                    e
                  ) => {
                    const value =
                      e.target.value.replace(
                        /\D/g,
                        ""
                      );

                    setAuthCode(
                      value
                    );
                  }}
                  placeholder="Enter 6-digit code"
                />

              </div>

              <div className="flex flex-wrap gap-3 mt-5">

                <button
                  type="button"
                  onClick={
                    handleVerifyAuthenticatorSetup
                  }
                  disabled={
                    authVerifyLoading ||
                    !authCode.trim()
                  }
                  className="bg-primaryBlue text-white text-sm px-6 py-4 rounded-md disabled:opacity-50"
                >
                  {authVerifyLoading
                    ? "Verifying..."
                    : "Verify & Enable"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setAuthSetupOpen(
                      false
                    );

                    setAuthSecret(
                      ""
                    );

                    setAuthOtpUri(
                      ""
                    );

                    setAuthCode(
                      ""
                    );
                  }}
                  disabled={
                    authVerifyLoading
                  }
                  className="border dark:border-[#c4c4c432] text-sm px-6 py-4 rounded-md"
                >
                  Cancel
                </button>

              </div>

            </div>

          )}

          {/* ================================================== */}
          {/* BACKUP / RECOVERY CODES */}
          {/* ================================================== */}

          {backupCodesOpen &&
            backupCodes.length >
              0 && (

            <div className="mt-6 border border-primaryBlue/30 bg-primaryBlue/5 dark:bg-primaryBlue/10 rounded-md p-6">

              <div>

                <p className="font-medium text-base">
                  Save your backup
                  codes
                </p>

                <p className="text-sm text-gray-500 mt-2">
                  Your authenticator
                  app has been
                  successfully
                  enabled. Save these
                  backup codes in a
                  secure location.
                </p>

                <p className="text-xs text-red-500 font-medium mt-3">
                  These codes are
                  shown only once.
                  Keep them somewhere
                  safe in case you
                  lose access to your
                  authenticator app.
                </p>

              </div>

              {/* CODES */}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">

                {backupCodes.map(
                  (
                    code,
                    index
                  ) => (

                    <div
                      key={`${code}-${index}`}
                      className="flex items-center gap-3 bg-white dark:bg-[#141D26] border dark:border-[#c4c4c432] rounded-md px-4 py-3"
                    >

                      <span className="text-xs text-gray-400 min-w-5">
                        {index +
                          1}
                        .
                      </span>

                      <span className="font-mono text-sm font-medium tracking-wider select-all">
                        {code}
                      </span>

                    </div>

                  )
                )}

              </div>

              {/* COPY BUTTON */}

              <div className="mt-5">

                <button
                  type="button"
                  onClick={
                    handleCopyBackupCodes
                  }
                  className="bg-primaryBlue text-white px-5 py-3 rounded-md text-sm hover:opacity-90 transition-all"
                >
                  {backupCodesCopied
                    ? "Copied to Clipboard ✓"
                    : "Copy All Codes"}
                </button>

              </div>

              {/* SAVE CONFIRMATION */}

              <div className="mt-5 pt-5 border-t border-primaryBlue/20 dark:border-[#c4c4c432]">

                <p className="text-xs text-gray-500 mb-4">
                  Confirm only after
                  you have copied or
                  securely stored the
                  codes above.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setBackupCodesOpen(
                      false
                    );

                    /*
                     * Remove the
                     * displayed codes
                     * from frontend
                     * memory.
                     */
                    setBackupCodes(
                      []
                    );

                    setBackupCodesCopied(
                      false
                    );
                  }}
                  className="border border-primaryBlue text-primaryBlue px-5 py-3 rounded-md text-sm hover:bg-primaryBlue/5 transition-all"
                >
                  I've saved these
                  codes
                </button>

              </div>

            </div>

          )}

          {/* ================================================== */}
          {/* PASSWORD CONFIRMATION WHEN LEAVING AUTHENTICATOR */}
          {/* ================================================== */}

          {showTwoFactorPasswordConfirm && (

            <div className="mt-5 border dark:border-[#c4c4c432] rounded-md p-5">

              <p className="font-medium text-sm">
                Confirm security
                change
              </p>

              <p className="text-xs text-gray-500 mt-2">

                Enter your current
                password to switch
                two-factor
                authentication to{" "}

                <span className="font-medium">
                  {pendingTwoFactorMethod ===
                  "none"
                    ? "None"
                    : "Email OTP"}
                </span>

                .

              </p>

              <div className="mt-4">

                <Input
                  label="Current password"
                  type="password"
                  isPassword="true"
                  value={
                    twoFactorPassword
                  }
                  onChange={(
                    e
                  ) =>
                    setTwoFactorPassword(
                      e.target
                        .value
                    )
                  }
                  placeholder="Enter your current password"
                />

              </div>

              <div className="flex flex-wrap gap-3 mt-4">

                <button
                  type="button"
                  disabled={
                    savingTwoFactor ||
                    !twoFactorPassword.trim()
                  }
                  onClick={() =>
                    handleTwoFactorChange(
                      pendingTwoFactorMethod,
                      twoFactorPassword
                    )
                  }
                  className="bg-primaryBlue text-white px-5 py-3 rounded-md text-sm disabled:opacity-50"
                >
                  {savingTwoFactor
                    ? "Updating..."
                    : "Confirm Change"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowTwoFactorPasswordConfirm(
                      false
                    );

                    setPendingTwoFactorMethod(
                      ""
                    );

                    setTwoFactorPassword(
                      ""
                    );
                  }}
                  className="border dark:border-[#c4c4c432] px-5 py-3 rounded-md text-sm"
                >
                  Cancel
                </button>

              </div>

            </div>

          )}

        </div>

        <hr className="my-10 dark:border-[#c4c4c432]" />

        {/* ==================================================== */}
        {/* UPDATE PASSWORD */}
        {/* ==================================================== */}

        <div>

          <p>
            Update password:
          </p>

          <div className="grid gap-4">

            <div className="grid grid-cols-1 gap-4 mt-6">

              <div>

                <Input
                  label={
                    "Old password"
                  }
                  type={
                    "password"
                  }
                  isPassword={
                    "true"
                  }
                  name={
                    "old_password"
                  }
                  value={
                    updateUserProfileForm
                      .values
                      .old_password
                  }
                  onBlur={
                    updateUserProfileForm.handleBlur
                  }
                  onChange={
                    updateUserProfileForm.handleChange
                  }
                  onError={
                    updateUserProfileForm
                      .errors
                      .old_password &&
                    updateUserProfileForm
                      .touched
                      .old_password
                      ? updateUserProfileForm
                          .errors
                          .old_password
                      : null
                  }
                />

              </div>

            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-4">

              <div>

                <Input
                  label={
                    "New password"
                  }
                  type={
                    "password"
                  }
                  isPassword={
                    "true"
                  }
                  name={
                    "new_password"
                  }
                  value={
                    updateUserProfileForm
                      .values
                      .new_password
                  }
                  onBlur={
                    updateUserProfileForm.handleBlur
                  }
                  onChange={
                    updateUserProfileForm.handleChange
                  }
                  onError={
                    updateUserProfileForm
                      .errors
                      .new_password &&
                    updateUserProfileForm
                      .touched
                      .new_password
                      ? updateUserProfileForm
                          .errors
                          .new_password
                      : null
                  }
                />

              </div>

              <div>

                <Input
                  label={
                    "Confirm password"
                  }
                  type={
                    "password"
                  }
                  isPassword={
                    "true"
                  }
                  name={
                    "confirm_password"
                  }
                  value={
                    updateUserProfileForm
                      .values
                      .confirm_password
                  }
                  onBlur={
                    updateUserProfileForm.handleBlur
                  }
                  onChange={
                    updateUserProfileForm.handleChange
                  }
                  onError={
                    updateUserProfileForm
                      .errors
                      .confirm_password &&
                    updateUserProfileForm
                      .touched
                      .confirm_password
                      ? updateUserProfileForm
                          .errors
                          .confirm_password
                      : null
                  }
                />

              </div>

              <div className="text-sm text-gray-500">

                <p>
                  Your password must
                  contain:
                </p>

                <ul className="list-disc list-inside">

                  <li>
                    at least 8
                    characters
                  </li>

                  <li>
                    at least 1
                    special character
                  </li>

                  <li>
                    at least 1 number
                  </li>

                  <li>
                    at least 1
                    uppercase alphabet
                  </li>

                </ul>

              </div>

            </div>

          </div>

          <button
            className="bg-primaryBlue text-white text-sm px-6 py-5 rounded-md mt-6"
            onClick={
              updateUserProfileForm.handleSubmit
            }
          >
            Update password
          </button>

        </div>

      </div>

      {/* ====================================================== */}
      {/* PASSWORD UPDATE LOADER */}
      {/* ====================================================== */}

      {loading && (

        <div>

          <Backdrop
            sx={{
              color:
                "#fff",

              zIndex: (
                theme
              ) =>
                theme.zIndex
                  .drawer +
                1,
            }}
            open={
              loading
            }
          >

            <CircularProgress color="inherit" />

            <p className="ml-3">
              Updating
              password...
            </p>

          </Backdrop>

        </div>

      )}

    </div>
  );
};

export default Settings;